import React, { memo, useEffect, useMemo } from "react";

// internal deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { replaceText } from "@swiggy-private/core-ui";
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";
import { FreebieWidgetLottie } from "./components";

// styles
import {
    BasePriceContainer,
    ButtonContainer,
    CardContainerWrapper,
    ClaimButton,
    FlexContainer,
    HeaderContainer,
    ImageContainer,
    ItemContainer,
    ItemDetailsContainer,
    ItemName,
    StyledImage,
} from "./FreebieWidget.styles";
import styles from "./FreebieWidget.scss";

//types
import { FreebieWidgetMeta } from "@swiggy-private/cart-types";

//constants
import { CROSS_SELL_M2_ITEM_PLACEHOLDER } from "@cart-ui/constants";
import { TEXTS } from "./constants";

//helpers
import { priceInRupee } from "@cart-ui/helpers/Price";
import { isMerchItem } from "@cart-ui/helpers/Item";

//test-ids
import { FREEBIE_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { getFreebieImageSize } from "./config";

interface FreebieWidgetProps {
    widgetData: FreebieWidgetMeta;
    onClaim: () => void;
    onImpression: () => void;
    isSnaccStorefrontV2?: boolean;
}

const FreebieWidget: React.FC<FreebieWidgetProps> = ({
    widgetData,
    onClaim,
    onImpression,
    isSnaccStorefrontV2,
}: FreebieWidgetProps) => {
    const title = replaceText(widgetData?.title || "", styles.imageStyle, true);
    const item = widgetData?.items[0];
    const itemPrice = item?.subtotal ?? item?.base_price ?? 0;
    const isMerchItemFlag = useMemo(() => isMerchItem(item || {}), [item]);
    const finalPrice = item?.final_price ?? 0;
    const isUnlockedState = widgetData?.movMet || false;
    const freebieImageSize = useMemo(() => getFreebieImageSize(isSnaccStorefrontV2), [isSnaccStorefrontV2]);
    const freebieCloudinaryHeight = useMemo(() => freebieImageSize.height * 2, [freebieImageSize.height]);
    useEffect(() => {
        // Trigger impression whenever lock state changes (both true and false)
        onImpression?.();
    }, [isUnlockedState, onImpression]);
    return (
        <CardContainerWrapper
            cornerRadius="medium"
            isUnlocked={isUnlockedState}
            data-testid={FREEBIE_WIDGET_TEST_IDS.CONTAINER}
        >
            {/* Lottie Animation Overlay */}
            <FreebieWidgetLottie isVisible={isUnlockedState} />

            <HeaderContainer
                data-testid={
                    isUnlockedState ? FREEBIE_WIDGET_TEST_IDS.UNLOCKED_STATE : FREEBIE_WIDGET_TEST_IDS.LOCKED_STATE
                }
            >
                {title && (
                    <Text
                        variant="V2.overline.neutral.black"
                        color={isUnlockedState ? "rating_Very_Good" : "special_button_text"}
                        style={{ textTransform: "uppercase" }}
                        data-testid={FREEBIE_WIDGET_TEST_IDS.TITLE}
                    >
                        {title}
                    </Text>
                )}

                {widgetData?.subtitle && (
                    <Text
                        variant="V2.body2.neutral.bold"
                        color="text_High_Emphasis"
                        data-testid={FREEBIE_WIDGET_TEST_IDS.SUBTITLE}
                    >
                        {widgetData?.subtitle}
                    </Text>
                )}
            </HeaderContainer>

            <ItemContainer>
                <div>
                    <ImageContainer isSnaccStorefrontV2={isSnaccStorefrontV2}>
                        <StyledImage
                            cloudinaryId={item?.cloudinaryImageId || ""}
                            errorFallbackSrc={CROSS_SELL_M2_ITEM_PLACEHOLDER}
                            fit="cover"
                            cloudinaryHeight={freebieCloudinaryHeight}
                            isSnaccStorefrontV2={isSnaccStorefrontV2}
                        />
                    </ImageContainer>
                </div>

                <ItemDetailsContainer>
                    {/* Show item name with veg/non-veg symbol for food items, without symbol for merch items */}

                    <Text
                        variant="V2.body2.neutral.medium"
                        color="text_Highest_Emphasis"
                        maxLines={2}
                        data-testid={FREEBIE_WIDGET_TEST_IDS.ITEM_NAME}
                    >
                        {!isMerchItemFlag && <FoodSymbolIconSvg vegType={item.isVeg} />}
                        <ItemName>{item?.name}</ItemName>
                    </Text>

                    <FlexContainer>
                        {/* logic for tote bag */}
                        {!isMerchItemFlag && (
                            <>
                                {itemPrice !== finalPrice && (
                                    <BasePriceContainer
                                        variant="V2.body2.neutral.medium"
                                        color="text_Low_Emphasis"
                                        data-testid={FREEBIE_WIDGET_TEST_IDS.ITEM_ORIGINAL_PRICE}
                                    >
                                        {`₹${priceInRupee(itemPrice)}`}
                                    </BasePriceContainer>
                                )}
                            </>
                        )}
                        <Text
                            variant="V2.body2.neutral.bold"
                            color="positive"
                            data-testid={FREEBIE_WIDGET_TEST_IDS.ITEM_FREE_TEXT}
                        >
                            {TEXTS.FREE}
                        </Text>
                    </FlexContainer>
                </ItemDetailsContainer>

                <ButtonContainer>
                    <ClaimButton
                        variant="primary"
                        isDisabled={!isUnlockedState}
                        onClick={onClaim}
                        data-testid={FREEBIE_WIDGET_TEST_IDS.CLAIM_BUTTON}
                    >
                        <Text
                            variant="V2.body2.neutral.bold"
                            color={!isUnlockedState ? "text_Button_Disabled" : "text_Highest_Emphasis"}
                            data-testid={FREEBIE_WIDGET_TEST_IDS.CLAIM_BUTTON_TEXT}
                        >
                            {TEXTS.CLAIM}
                        </Text>
                    </ClaimButton>
                </ButtonContainer>
            </ItemContainer>
        </CardContainerWrapper>
    );
};

export default memo(FreebieWidget);
